import React from 'react';
import { 
  Wifi, 
  Car, 
  Users, 
  Bike, 
  UtensilsCrossed, 
  Heart, 
  TreePine, 
  Home,
  Wine,
  Sparkles,
  Award,
  Calendar,
  CreditCard,
  ChefHat,
  Coffee,
  Flame,
  Phone,
  Fish,
  Leaf,
  Star,
  MapPin,
  Baby,
  Package,
  Clock,
  ShoppingBag
} from 'lucide-react';
import { FadeInUp, BounceIn } from './ScrollAnimationWrapper';

const Amenities = ({ activeSection = 'hotel', language = 'de' }) => {
  
  // Prevodi za Hotel sekciju
  const hotelTranslations = {
    de: {
      title: 'Unsere Annehmlichkeiten',
      subtitle: 'Alles was Sie für einen komfortablen und unvergesslichen Aufenthalt brauchen',
      amenities: [
        { icon: Wifi, title: 'Kostenloses WLAN', description: 'Schnelles Internet inklusive' },
        { icon: Car, title: 'Gratis Parkplatz', description: 'Bequemes Parken vor Ort' },
        { icon: Users, title: 'Familienfreundlich', description: 'Perfekt für Familien mit Kindern' },
        { icon: Bike, title: 'Fahrradrouten', description: 'Schöne Trails in der Umgebung' },
        { icon: UtensilsCrossed, title: 'Italienisches Restaurant', description: 'Authentisches La Casa Erlebnis' },
        { icon: TreePine, title: 'Naturlage', description: 'Umgeben von malerischer Landschaft' },
        { icon: MapPin, title: 'Wandern', description: 'Wanderwege in der Umgebung' },
        { icon: Home, title: 'Gemütliche Zimmer', description: 'Komfortable Unterkünfte' }
      ]
    },
    en: {
      title: 'Our Amenities',
      subtitle: 'Everything you need for a comfortable and memorable stay',
      amenities: [
        { icon: Wifi, title: 'Free WiFi', description: 'Complimentary high-speed internet' },
        { icon: Car, title: 'Free Parking', description: 'Convenient on-site parking' },
        { icon: Users, title: 'Family Friendly', description: 'Perfect for families with children' },
        { icon: Bike, title: 'Cycling Routes', description: 'Beautiful trails in the area' },
        { icon: UtensilsCrossed, title: 'Italian Restaurant', description: 'Authentic La Casa dining experience' },
        { icon: TreePine, title: 'Nature Location', description: 'Surrounded by scenic countryside' },
        { icon: MapPin, title: 'Hiking', description: 'Hiking trails nearby' },
        { icon: Home, title: 'Cozy Rooms', description: 'Comfortable accommodations' }
      ]
    }
  };

  // Prevodi za Restaurant sekciju - BRUTAL + Mobile Optimized
  const restaurantTranslations = {
    de: {
      title: 'Ristorante La Casa',
      subtitle: 'Wo Authentizität auf Moderne trifft',
      mainFeatures: [
        {
          icon: ChefHat,
          title: 'Authentisch Italienisch',
          items: [
            { icon: UtensilsCrossed, text: 'Frische Pasta' },
            { icon: Fish, text: 'Fisch & Meeresfrüchte' },
            { icon: Leaf, text: 'Saisonale Zutaten' },
            { icon: Star, text: 'Vor Ort Essen' }
          ]
        },
        {
          icon: Coffee,
          title: 'Erlesene Getränke',
          items: [
            { icon: Wine, text: 'Italienische Weine' },
            { icon: Coffee, text: 'Premium Kaffee' },
            { icon: Award, text: 'Hausgemachte Desserts' },
            { icon: Heart, text: 'Perfekte Paarung' }
          ]
        },
        {
          icon: Flame,
          title: 'Atmosphäre',
          items: [
            { icon: Heart, text: 'Romantisch' },
            { icon: Home, text: 'Gemütlich' },
            { icon: Star, text: 'Elegant' },
            { icon: MapPin, text: 'Außenbereich' }
          ]
        },
        {
          icon: Phone,
          title: 'Services',
          items: [
            { icon: Baby, text: 'Kinderfreundlich' },
            { icon: Users, text: 'Gruppen willkommen' },
            { icon: Clock, text: 'Reservierungen' },
            { icon: ShoppingBag, text: 'Zum Mitnehmen' }
          ]
        }
      ],
      bottomInfo: [
        { icon: Car, text: 'Kostenlose Parkplätze' },
        { icon: CreditCard, text: 'EC & Kreditkarten' },
        { icon: Calendar, text: 'Reservierungen empfohlen' },
        { icon: Award, text: 'Authentische Küche' }
      ]
    },
    en: {
      title: 'Ristorante La Casa',
      subtitle: 'Where Authenticity Meets Modernity',
      mainFeatures: [
        {
          icon: ChefHat,
          title: 'Authentic Italian',
          items: [
            { icon: UtensilsCrossed, text: 'Fresh Pasta' },
            { icon: Fish, text: 'Fish & Seafood' },
            { icon: Leaf, text: 'Seasonal Ingredients' },
            { icon: Star, text: 'Dine In' }
          ]
        },
        {
          icon: Coffee,
          title: 'Fine Beverages',
          items: [
            { icon: Wine, text: 'Italian Wines' },
            { icon: Coffee, text: 'Premium Coffee' },
            { icon: Award, text: 'Homemade Desserts' },
            { icon: Heart, text: 'Perfect Pairing' }
          ]
        },
        {
          icon: Flame,
          title: 'Atmosphere',
          items: [
            { icon: Heart, text: 'Romantic' },
            { icon: Home, text: 'Cozy' },
            { icon: Star, text: 'Elegant' },
            { icon: MapPin, text: 'Outdoor Seating' }
          ]
        },
        {
          icon: Phone,
          title: 'Services',
          items: [
            { icon: Baby, text: 'Kid Friendly' },
            { icon: Users, text: 'Groups Welcome' },
            { icon: Clock, text: 'Reservations' },
            { icon: ShoppingBag, text: 'Takeaway' }
          ]
        }
      ],
      bottomInfo: [
        { icon: Car, text: 'Free Parking' },
        { icon: CreditCard, text: 'Cards Accepted' },
        { icon: Calendar, text: 'Reservations Recommended' },
        { icon: Award, text: 'Authentic Cuisine' }
      ]
    }
  };

  const ht = hotelTranslations[language];
  const rt = restaurantTranslations[language];

  return (
    <section className={`section-padding ${activeSection === 'restaurant' ? 'bg-gradient-to-b from-luxury-charcoal via-black/80 to-luxury-charcoal' : 'bg-gradient-to-b from-luxury-cream via-luxury-pearl/60 to-luxury-cream'}`}>
      <div className="container-custom">
        
        {activeSection === 'hotel' && (
          <>
            {/* Hotel Amenities Header */}
            <FadeInUp threshold={0.1} className="text-center mb-20">
              <h2 className="text-5xl md:text-6xl font-display font-bold text-luxury-charcoal mb-6">
                {ht.title}
              </h2>
              <div className="w-32 h-1 bg-luxury-gold mx-auto mb-8" />
              <p className="text-xl md:text-2xl text-luxury-slate max-w-4xl mx-auto leading-relaxed font-serif">
                {ht.subtitle}
              </p>
            </FadeInUp>

            {/* Hotel Amenities Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
              {ht.amenities.map((amenity, index) => (
                <BounceIn 
                  key={index} 
                  delay={index * 150} 
                  threshold={0.1}
                  className="group h-full"
                >
                  <div className="h-full bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-white/20 glassmorphism">
                    <div className="h-full flex flex-col items-center text-center">
                      <div className="mb-6 p-4 bg-gradient-to-br from-luxury-gold to-luxury-darkGold rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <amenity.icon className="text-white" size={32} />
                      </div>
                      <h3 className="text-xl font-display font-bold text-luxury-charcoal mb-3">
                        {amenity.title}
                      </h3>
                      <p className="text-luxury-slate text-sm leading-relaxed">
                        {amenity.description}
                      </p>
                    </div>
                  </div>
                </BounceIn>
              ))}
            </div>
          </>
        )}

        {activeSection === 'restaurant' && (
          <>
            {/* BRUTAL Restaurant Header - Dark Theme */}
            <FadeInUp threshold={0.1} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-display font-black text-white mb-6 tracking-tight leading-none drop-shadow-xl">
                {rt.title}
              </h2>
              <div className="w-24 h-1 bg-luxury-gold mx-auto mb-6 shadow-lg" />
              <p className="text-lg md:text-xl text-luxury-cream max-w-3xl mx-auto leading-relaxed font-serif font-bold">
                {rt.subtitle}
              </p>
            </FadeInUp>

            {/* LUXURY BLACK GRID - Restaurant Features */}
            <div className="max-w-6xl mx-auto mb-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {rt.mainFeatures.map((feature, index) => (
                  <BounceIn key={index} delay={index * 150} threshold={0.1} className="h-full">
                    <div className="h-full group relative bg-gradient-to-br from-luxury-charcoal via-black/90 to-luxury-slate rounded-2xl p-8 border border-luxury-gold/40 shadow-xl hover:shadow-luxury-gold/20 transition-all duration-500 hover:-translate-y-2 overflow-hidden backdrop-blur-lg">
                      
                      {/* Luxury Background Effects */}
                      <div className="absolute inset-0 bg-gradient-to-br from-luxury-gold/5 via-transparent to-luxury-gold/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="absolute -top-10 -right-10 w-32 h-32 bg-luxury-gold/10 rounded-full blur-2xl group-hover:bg-luxury-gold/20 transition-all duration-500" />
                      <div className="absolute -bottom-5 -left-5 w-20 h-20 bg-luxury-cream/5 rounded-full blur-xl" />
                      
                      {/* Content */}
                      <div className="relative z-10">
                        {/* Luxury Icon */}
                        <div className="mb-6">
                          <div className="w-16 h-16 bg-gradient-to-br from-luxury-gold to-luxury-darkGold rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300 border border-luxury-gold/50">
                            <feature.icon className="text-white" size={24} strokeWidth={2} />
                          </div>
                        </div>

                        {/* Title */}
                        <h3 className="text-xl md:text-2xl font-display font-black text-white mb-6 uppercase tracking-wide drop-shadow-lg">
                          {feature.title}
                        </h3>

                        {/* Luxury Items Grid */}
                        <div className="space-y-3">
                          {feature.items.map((item, idx) => (
                            <div 
                              key={idx}
                              className="group/item flex items-center gap-3 p-3 bg-luxury-slate/30 backdrop-blur-sm rounded-xl border border-luxury-gold/30 hover:bg-luxury-gold/10 hover:border-luxury-gold/60 transition-all duration-300 hover:translate-x-1"
                            >
                              <div className="w-6 h-6 bg-luxury-gold/20 rounded-full flex items-center justify-center group-hover/item:bg-luxury-gold/40 transition-colors duration-300">
                                <item.icon className="text-luxury-gold" size={12} />
                              </div>
                              <span className="text-luxury-cream font-semibold text-sm group-hover/item:text-white transition-colors duration-300">
                                {item.text}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Luxury Border Glow Effect */}
                      <div className="absolute inset-0 rounded-2xl border border-luxury-gold/0 group-hover:border-luxury-gold/60 transition-all duration-300 pointer-events-none" />
                    </div>
                  </BounceIn>
                ))}

              </div>
            </div>

            {/* LUXURY INFO BAR - Dark Theme */}
            <FadeInUp delay={600} threshold={0.1}>
              <div className="max-w-5xl mx-auto bg-gradient-to-r from-luxury-charcoal via-luxury-slate/80 to-luxury-charcoal rounded-2xl border border-luxury-gold/40 p-6 shadow-xl backdrop-blur-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {rt.bottomInfo.map((info, index) => (
                    <BounceIn key={index} delay={700 + index * 75} threshold={0.1}>
                      <div className="group flex items-center gap-3 p-4 bg-luxury-slate/40 backdrop-blur-sm rounded-xl border border-luxury-gold/30 hover:bg-luxury-gold/10 hover:border-luxury-gold/60 transition-all duration-300 hover:scale-105">
                        <div className="w-8 h-8 bg-luxury-gold/20 rounded-lg flex items-center justify-center group-hover:bg-luxury-gold/40 transition-colors duration-300">
                          <info.icon className="text-luxury-gold" size={18} strokeWidth={2} />
                        </div>
                        <span className="text-luxury-cream font-bold uppercase tracking-wide text-xs group-hover:text-white transition-colors duration-300">
                          {info.text}
                        </span>
                      </div>
                    </BounceIn>
                  ))}
                </div>
              </div>
            </FadeInUp>
          </>
        )}

      </div>
    </section>
  );
};

export default Amenities;
